import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { reputationData } from '@/mocks/home';

export default function AboutPage() {
  const [formState, setFormState] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt')?.toString().trim() || '';
    if (honeypot) {
      setStatus('success');
      setStatusMessage('提交成功！我们会尽快与你联系。');
      setFormState({ name: '', email: '', message: '' });
      return;
    }

    setStatus('loading');
    try {
      const res = await fetch('https://readdy.ai/api/form/d9obrjobdkpk99j7gtq0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          name: formState.name,
          email: formState.email,
          message: formState.message,
        }).toString(),
      });
      const responseText = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setStatusMessage('提交成功！我们会尽快与你联系。');
        setFormState({ name: '', email: '', message: '' });
      } else if (!res.ok || (parsed && parsed.code !== 'OK')) {
        setStatus('error');
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setStatusMessage(`提交失败：${serverMsg}`);
      } else {
        setStatus('success');
        setStatusMessage('提交成功！我们会尽快与你联系。');
        setFormState({ name: '', email: '', message: '' });
      }
    } catch {
      setStatus('error');
      setStatusMessage('网络错误，请稍后重试。');
    }
  };

  return (
    <>
      <Helmet>
        <title>关于我们 - 夜猫电影院 | 品牌介绍 · 口碑评价 · 联系方式</title>
        <meta name="description" content="夜猫电影院品牌介绍——专为夜间追剧打造的免费在线影院平台。了解我们的故事、用户口碑和联系方式。深夜影院，高清不卡顿。" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于夜猫电影院',
            description: '夜猫电影院专为夜间追剧打造，免费在线观看高清电影、电视剧。了解品牌故事、用户口碑和联系方式。',
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://yemaocinema.com/' },
              { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://yemaocinema.com/about' },
            ],
          })}
        </script>
      </Helmet>

      <main className="min-h-screen bg-background-950 pt-16">
        {/* Breadcrumb */}
        <div className="bg-background-900 border-b border-background-700/30">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-600">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-400">关于我们</span>
            </nav>
          </div>
        </div>

        {/* Brand Intro */}
        <section className="py-14 md:py-20 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-primary-500/10 text-primary-500 mb-4">
                品牌故事
              </span>
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-50 mb-4">
                关于夜猫电影院
              </h1>
              <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                专为夜猫子打造的在线观影平台，让每一个深夜都有好片相伴
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-16">
              <div className="relative aspect-[4/3] rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20cinema%20lounge%20at%20night%20with%20warm%20golden%20lighting%2C%20comfortable%20red%20velvet%20seats%2C%20atmospheric%20movie%20theater%20interior%2C%20elegant%20dark%20aesthetic%2C%20luxurious%20cinema%20design&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="夜猫电影院品牌形象"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-50 mb-4">
                  成为夜猫子的观影首选
                </h2>
                <p className="text-foreground-500 text-sm md:text-base leading-relaxed mb-4">
                  夜猫电影院诞生于一个深夜——创始人作为一个资深夜猫子，深感深夜观影时常常遇到缓冲卡顿、片源不全的困扰。于是，他决定为所有和他一样的夜猫子们，打造一个真正懂深夜观影者的平台。
                </p>
                <p className="text-foreground-500 text-sm md:text-base leading-relaxed mb-4">
                  我们深知，深夜是一天中最珍贵的自由时光。没有工作的打扰，没有社交的压力，只有你和银幕上的故事。夜猫电影院的存在，就是为了守护这份独属于夜猫子的观影仪式感。
                </p>
                <p className="text-foreground-500 text-sm md:text-base leading-relaxed">
                  从创立至今，我们始终坚持<strong className="text-foreground-300">免费、高清、不卡顿</strong>三大原则，用技术实力和内容积累，为数百万夜猫子提供了优质的深夜观影体验。
                </p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
              {[
                { value: '500万+', label: '月活跃用户' },
                { value: '3万+', label: '影视资源' },
                { value: '99.9%', label: '播放成功率' },
                { value: '24/7', label: '全天候服务' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-5 rounded-xl bg-background-800/40 border border-background-700/30">
                  <p className="text-2xl md:text-3xl font-heading font-bold text-accent-500 mb-1">{stat.value}</p>
                  <p className="text-foreground-600 text-xs">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section id="reputation" className="py-14 md:py-20 px-4 md:px-6 bg-background-900">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-500/10 text-accent-500 mb-3">
                用户口碑
              </span>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50 mb-3">
                夜猫子们怎么说
              </h2>
              <p className="text-foreground-600 text-sm md:text-base">
                来自真实用户的观影反馈，是我们前进的最大动力
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {reputationData.map((item, i) => (
                <div key={i} className="p-5 md:p-6 rounded-xl bg-background-800/40 border border-background-700/30">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: item.rating }).map((_, j) => (
                      <div key={j} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-foreground-400 text-sm leading-relaxed mb-4">
                    &ldquo;{item.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <img src={item.avatar} alt={item.name} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <p className="text-foreground-200 text-sm font-medium">{item.name}</p>
                      <p className="text-foreground-600 text-xs">{item.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-14 md:py-20 px-4 md:px-6">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-secondary-500/10 text-secondary-500 mb-3">
                联系我们
              </span>
              <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50 mb-3">
                期待你的声音
              </h2>
              <p className="text-foreground-600 text-sm md:text-base">
                有任何建议、合作意向或问题反馈，欢迎随时联系我们
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-foreground-400 text-sm mb-1.5">你的名字</label>
                  <input
                    type="text"
                    name="name"
                    value={formState.name}
                    onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                    required
                    placeholder="请输入你的名字"
                    className="w-full px-4 py-3 text-sm rounded-lg bg-background-800/80 border border-background-600/40 text-foreground-200 placeholder:text-foreground-700 focus:outline-none focus:border-primary-500/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-foreground-400 text-sm mb-1.5">邮箱地址</label>
                  <input
                    type="email"
                    name="email"
                    value={formState.email}
                    onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                    required
                    placeholder="请输入你的邮箱"
                    className="w-full px-4 py-3 text-sm rounded-lg bg-background-800/80 border border-background-600/40 text-foreground-200 placeholder:text-foreground-700 focus:outline-none focus:border-primary-500/50 transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-foreground-400 text-sm mb-1.5">留言内容</label>
                <textarea
                  name="message"
                  value={formState.message}
                  onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请输入你的留言内容（最多500字）"
                  className="w-full px-4 py-3 text-sm rounded-lg bg-background-800/80 border border-background-600/40 text-foreground-200 placeholder:text-foreground-700 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                />
                <p className="text-foreground-700 text-xs mt-1 text-right">{formState.message.length}/500</p>
              </div>
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="website-alt-field"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full py-3 text-sm font-medium rounded-lg bg-primary-500 text-background-950 hover:bg-primary-600 transition-all whitespace-nowrap disabled:opacity-60 cursor-pointer"
              >
                {status === 'loading' ? '提交中...' : '发送留言'}
              </button>
              {status === 'success' && (
                <p className="text-center text-sm text-primary-500 font-medium">{statusMessage}</p>
              )}
              {status === 'error' && (
                <p className="text-center text-sm text-accent-500 font-medium">{statusMessage}</p>
              )}
            </form>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12">
              <div className="text-center p-5 rounded-xl bg-background-800/40 border border-background-700/30">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-500/10 mx-auto mb-3">
                  <i className="ri-mail-line text-xl text-primary-500"></i>
                </div>
                <h4 className="text-foreground-200 text-sm font-medium mb-1">官方邮箱</h4>
                <p className="text-foreground-600 text-xs">contact@yemaocinema.com</p>
              </div>
              <div className="text-center p-5 rounded-xl bg-background-800/40 border border-background-700/30">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-accent-500/10 mx-auto mb-3">
                  <i className="ri-wechat-line text-xl text-accent-500"></i>
                </div>
                <h4 className="text-foreground-200 text-sm font-medium mb-1">微信公众号</h4>
                <p className="text-foreground-600 text-xs">夜猫电影院官方</p>
              </div>
              <div className="text-center p-5 rounded-xl bg-background-800/40 border border-background-700/30">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-secondary-500/10 mx-auto mb-3">
                  <i className="ri-time-line text-xl text-secondary-500"></i>
                </div>
                <h4 className="text-foreground-200 text-sm font-medium mb-1">服务时间</h4>
                <p className="text-foreground-600 text-xs">24小时全天候在线</p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}